import { AxiosError } from 'axios';
import * as React from 'react';
import styled from 'styled-components';

import useProgressBar from 'src/CustomHooks/useProgressBar';
import AttributionContainer from 'src/SharedComponents/AttributionContainer';
import Button from 'src/SharedComponents/Button';
import ButtonContainer from 'src/SharedComponents/ButtonContainer';
import ContentWrapper from 'src/SharedComponents/ContentWrapper';
import ErrorToast from 'src/SharedComponents/ErrorToast';
import MultipleChoiceQuestion from 'src/SharedComponents/MultipleChoiceQuestion';
import ProgressBar from 'src/SharedComponents/ProgressBar';
import QuestionList from 'src/SharedComponents/QuestionList';

import { saveAnswer } from 'src/service';
import { SessionContext } from 'src/Views/SessionContext';


const Title = styled.h1`
    margin: 12px 12px 0;
    font-size: 1.6rem;
`;

const Introduction = styled.p`
    font-size: 1.1rem;
    margin: 6px 12px;
`;

const FreeTextQuestion = styled.div`
    padding: 6px 12px;
    font-size: 1.1rem;
    display: flex;
    flex-direction: column;
`;

const FreeTextInput = styled.textarea`
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    width: 100%;
    height: 80px;
`;

const answers = ["Not true", "Somewhat true", "Certainly true"];
// All except the free text
const requiredAnswers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];

const SdqFormPage1 = () => {
    const { dispatch, state } = React.useContext(SessionContext);
    const [error, setError] = React.useState("");
    const { answered, numberAnswered, updateAnswered, getAnswered, includedInArray, ERROR_SAVING_MESSAGE } = useProgressBar();
    const questionRefs = requiredAnswers.map(questionId => React.useRef(null as unknown as HTMLLIElement));

    const checkUnauthorised = (serverError: AxiosError) => {
        if (serverError.response && serverError.response.data.code === 401) {
            dispatch({ type: "clearWebToken", payload: undefined });
        }
    }

    const handleError = (serverError: AxiosError, questionId: number) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE)
        updateAnswered(questionId, false, -1);
    };

    const onAnswerChange = (questionId: number, answer: string, itemIndex?: number) => {
        setError("");
        saveAnswer(questionId, answer, state.webToken, () => {
            updateAnswered(questionId, !!answer, itemIndex !== undefined ? itemIndex : -1);
        }, handleError)
    };

    const handleFreeTextChange = (questionId: number) => (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onAnswerChange(questionId, event.currentTarget.value);
    };

    const goToNextPage = () => {
        dispatch({ type: "setPage", payload: { formId: 1, page: 2 } });
    };

    const onDisabledClick = () => {
        const firstUnansweredId = requiredAnswers.find(requiredAnswerId => !includedInArray(answered, requiredAnswerId));
        if (firstUnansweredId !== undefined) {
            questionRefs[firstUnansweredId].current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setError("Please answer all questions");
        }
    };

    const requiredQuestionsAnswered = () => {
        return requiredAnswers.every(item => includedInArray(answered, item));
    };

    return <ContentWrapper>
        <Title>Strengths and difficulties</Title>
        <Introduction>
            For each item, please mark the box for Not true, Somewhat true or Certainly true. It would help us if you answered all items as
            best you can even if you are not absolutely certain or the item seems daft! Please give your answers on the basis of how things
            have been for you over the last month.
        </Introduction>
        <QuestionList>
            <MultipleChoiceQuestion refProp={questionRefs[0]} checkedItem={getAnswered(0)} question="I try to be nice to other people. I care about their feelings" questionId={0} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[1]} checkedItem={getAnswered(1)} question="I am restless, I cannot stay still for long" questionId={1} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[2]} checkedItem={getAnswered(2)} question="I get a lot of headaches, stomach-aches or sickness" questionId={2} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[3]} checkedItem={getAnswered(3)} question="I usually share with others (food, games, pens, etc.)" questionId={3} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[4]} checkedItem={getAnswered(4)} question="I get very angry and often lose my temper" questionId={4} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[5]} checkedItem={getAnswered(5)} question="I am usually on my own. I generally play alone or keep to myself" questionId={5} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[6]} checkedItem={getAnswered(6)} question="I usually do as I am told" questionId={6} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[7]} checkedItem={getAnswered(7)} question="I worry a lot" questionId={7} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[8]} checkedItem={getAnswered(8)} question="I am helpful if someone is hurt, upset or feeling ill" questionId={8} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[9]} checkedItem={getAnswered(9)} question="I am constantly fidgeting or squirming" questionId={9} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[10]} checkedItem={getAnswered(10)} question="I have one good friend or more" questionId={10} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[11]} checkedItem={getAnswered(11)} question="I fight a lot. I can make other people do what I want" questionId={11} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[12]} checkedItem={getAnswered(12)} question="I am often unhappy, down-hearted or tearful" questionId={12} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[13]} checkedItem={getAnswered(13)} question="Other people my age generally like me" questionId={13} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[14]} checkedItem={getAnswered(14)} question="I am easily distracted, I find it difficult to concentrate" questionId={14} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[15]} checkedItem={getAnswered(15)} question="I am nervous in new situations. I easily lose confidence" questionId={15} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[16]} checkedItem={getAnswered(16)} question="I am kind to younger children" questionId={16} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[17]} checkedItem={getAnswered(17)} question="I am often accused of lying or cheating" questionId={17} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[18]} checkedItem={getAnswered(18)} question="Other children or young people pick on me or bully me" questionId={18} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[19]} checkedItem={getAnswered(19)} question="I often volunteer to help others (parents, teachers, children)" questionId={19} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[20]} checkedItem={getAnswered(20)} question="I think before I do things" questionId={20} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[21]} checkedItem={getAnswered(21)} question="I take things that are not mine from home, school or elsewhere" questionId={21} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[22]} checkedItem={getAnswered(22)} question="I get on better with adults than with people my own age" questionId={22} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[23]} checkedItem={getAnswered(23)} question="I have many fears, I am easily scared" questionId={23} answers={answers} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion refProp={questionRefs[24]} checkedItem={getAnswered(24)} question="I finish the work I am doing. My attention is good" questionId={24} answers={answers} onAnswerChange={onAnswerChange} />
        </QuestionList>
        <FreeTextQuestion>
            <label>
                <p>Do you have any other comments or concerns?</p>
                <FreeTextInput onBlur={handleFreeTextChange(25)} />
            </label>
        </FreeTextQuestion>
        <ButtonContainer>
            <Button text="Next page" onClick={goToNextPage} onDisabledClick={onDisabledClick} disabled={!requiredQuestionsAnswered()} />
        </ButtonContainer>
        <ProgressBar length={25} progress={numberAnswered} />
        <ErrorToast role="alert" aria-atomic="true" className={error ? "sliding" : "hidden"}>{error}</ErrorToast>
        <AttributionContainer>
            The Strengths and Difficulties Questionnaire (SDQ) is copyright of Youthinmind Limited
        </AttributionContainer>
    </ContentWrapper>;
};

export default SdqFormPage1;


// WEBPACK FOOTER //
// ./src/Forms/sdq/SdqFormPage1.tsx